import { useProjectActions, useProjectContext } from "@contexts/projects";
import { downloadCsv } from "@utils/downloadCsv";
import { formatNumberToCurrency } from "@utils/formatNumber";
import { formatCreditorName } from "@utils/creditorName";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { parseTitleDeepLink } from "./titleDeepLink";

const renderRow = (label, value) => {
  return <div className="data">
    <span className="accordion-label">{label}</span>
    <span className="value">{value}</span>
  </div>
}

const Item = ({ index, resource, paid }) => {
  const [open, setOpen] = useState(false)
  const { activeProject } = useProjectContext()
  const { getWbsPath } = useProjectActions()
  const [details, setDetails] = useState({})

  const getPath = async () => {
    const path = `wbs_code_id_${activeProject?.appropriation_level}`
    const name = resource.wbs_code_id_building
    const wbs = resource[path].replace(name, '')
    const res = await getWbsPath(wbs, name)
    setDetails(res.response)
  }

  // useEffect(() => {
  //   if (open) {
  //     getPath()
  //   }
  // }, [open])

  const getLabel = (label) => {
    switch (label) {
      case 'unit':
        return 'Unidade'
      case 'cell':
        return 'Célula'
      case 'step':
        return 'Etapa'
      case 'substep':
        return 'Subetapa'
      case 'service':
        return 'Serviço'
      default:
        break;
    }
  }

  return <div key={index} className={`accordion-item offcanvas-accordion-item curve margin-bottom-8`}>
    <div>
      <div className="flex align-center margin-bottom-8">
        <button
          className={`accordion-toggle ${open ? "active" : ""}`}
          onClick={() => setOpen(!open)}
        />
        <span className="accordion-title" title={formatCreditorName(resource.creditor_name)}>{formatCreditorName(resource.creditor_name)}</span>
      </div>
    </div>
    <div className="grid gap-16 columns-5-laptop-l margin-top-12">
      {renderRow(`Documento`, resource.document)}
      {renderRow(`Título`, resource.id)}
      {renderRow('Data de registro', resource?.registered_date?.split('-').reverse().join('/'))}
      {renderRow(`Data de ${paid ? 'pagamento' : 'vencimento'}`, resource?.date?.split('-').reverse().join('/'))}
      {renderRow('Valor', formatNumberToCurrency(resource.value))}
    </div>
    {open && <div className={`grid gap-16 columns-1-laptop-l margin-top-12`}>
      {Object.entries(resource.wbs_info).map(([label, value]) => {
        if (value === undefined) return null
        return renderRow(getLabel(label), value)
      })}
    </div>}
  </div>
}

const ItemsTimeline = () => {
  const { getTimeline, getAllBuildingItems } = useProjectActions();
  const { activeProject } = useProjectContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialDeepLink] = useState(() => parseTitleDeepLink(searchParams));
  const [dateLimit, setDateLimit] = useState({
    max: 0,
    min: 0,
  });
  const [resources, setResources] = useState([]);
  const [view, setView] = useState(initialDeepLink?.view ?? 'paid')
  const [search, setSearch] = useState({
    search: initialDeepLink?.search.search ?? '',
    searchFor: initialDeepLink?.search.searchFor ?? '',
  })
  const [appliedSearch, setAppliedSearch] = useState({
    search: initialDeepLink?.search.search ?? '',
    searchFor: initialDeepLink?.search.searchFor ?? '',
  })
  const [documents, setDocuments] = useState([])
  const [docFilter, setDocFilter] = useState('')
  const [levelToGet, setLevelToGet] = useState('')
  const [levelItems, setLevelItems] = useState([])
  const [itemToFilter, setItemToFilter] = useState('')
  const countRef = useRef(0);
  const activeWbsKey = (activeProject?.wbs_code_ids ?? []).join('|');
  const isFiltering = Boolean(
    dateLimit.max !== 0 ||
    dateLimit.min !== 0 ||
    docFilter !== '' ||
    appliedSearch.search !== '' ||
    itemToFilter !== ''
  );

  useEffect(() => {
    let cancelled = false;
    let loadingPage = false;
    const filtering = Boolean(
      dateLimit.max !== 0 ||
      dateLimit.min !== 0 ||
      docFilter !== '' ||
      appliedSearch.search !== '' ||
      itemToFilter !== ''
    );
    setResources([]);
    countRef.current = 0;

    const getItems = async () => {
      if (loadingPage || !activeProject?.wbs_code_ids?.length) return;
      loadingPage = true;
      try {
        const response = await getTimeline(
          activeProject.wbs_code_ids,
          '',
          view,
          dateLimit,
          countRef.current,
          docFilter,
          appliedSearch,
          levelToGet.slice(0, -1),
          itemToFilter,
        );
        if (cancelled) return;
        countRef.current += 50;
        const next = [...(response?.res ?? [])].sort(
          (a, b) => new Date(b?.registered_date) - new Date(a?.registered_date)
        );
        setResources((previous) => [...previous, ...next]);
        setDocuments(response?.uniqueDocs ?? []);
      } finally {
        loadingPage = false;
      }
    };

    const handleScroll = () => {
      if (filtering) return;
      const scrolledTo = window.scrollY + window.innerHeight;
      if (scrolledTo >= document.body.scrollHeight) getItems();
    };

    getItems();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      cancelled = true;
      window.removeEventListener("scroll", handleScroll);
    };
  }, [
    activeProject?._id,
    activeWbsKey,
    view,
    dateLimit.max,
    dateLimit.min,
    docFilter,
    appliedSearch.search,
    appliedSearch.searchFor,
    levelToGet,
    itemToFilter,
  ]);

  const handleReset = () => {
    setSearch({ search: '', searchFor: '' });
    setAppliedSearch({ search: '', searchFor: '' });
    setDateLimit({ max: 0, min: 0 });
    setDocFilter('');
    setSearchParams({}, { replace: true });
  };

  const handleWbsReset = () => {
    setLevelToGet('')
    setLevelItems([])
    setItemToFilter('')
  }

  const handleDateChange = (e) => {
    const { name, value } = e.target
    setDateLimit(prev => ({ ...prev, [name]: value }))
  }

  const handleDownload = async (e) => {
    const fields = [
      { key: 'creditor_name', label: "Nome" },
      { key: 'id', label: "Título" },
      { key: 'value', label: "Valor" },
      { key: 'document', label: "Documento" },
      { key: 'registered_date', label: "Registrado em" },
      { key: 'date', label: "Data" },
      { key: 'unit', label: "Unidade" },
      { key: 'cell', label: "Célula" },
      { key: 'step', label: "Etapa" },
      { key: 'substep', label: "Subetapa" },
      { key: 'service', label: "Serviço" },
    ]
    if (isFiltering) {
      const format = resources.map(i => ({
        ...i,
        date: i.date.split('-').reverse().join('/'),
        registered_date: i.registered_date.split('-').reverse().join('/'),
        unit: i.wbs_info.unit,
        cell: i.wbs_info.cell,
        step: i.wbs_info.step,
        substep: i.wbs_info.substep,
        service: i.wbs_info.service,
      }))
      downloadCsv(e, fields, format)
    } else {
      const allItems = await getTimeline(activeProject?.wbs_code_ids, '', view, { min: '1990-01-01', max: '2099-01-01' }, countRef.current, docFilter, { search: '', searchFor: '' });
      const format = allItems?.res?.sort(
        (a, b) => new Date(b.registered_date) - new Date(a.registered_date)
      ).map(i => ({
        ...i,
        date: i.date.split('-').reverse().join('/'),
        registered_date: i.registered_date.split('-').reverse().join('/')
      }))
      downloadCsv(e, fields, format)
    }
  }

  const handleFilter = () => {
    setAppliedSearch({ ...search });
  }

  const handleEditSearch = (e) => {
    const { name, value } = e.target
    setSearch(prev => ({ ...prev, [name]: value }))
  }

  const getLevelItems = async () => {
    const res = await getAllBuildingItems([levelToGet])
    setLevelItems(res.items)
  }

  useEffect(() => {
    if (levelToGet !== '') {
      getLevelItems()
    }
  }, [levelToGet])

  const handleWbsSearch = (e) => {
    setItemToFilter(e.target.value)
  }

  const handleViewChange = (nextView) => {
    setView(nextView);
    if (appliedSearch.searchFor === 'id' && appliedSearch.search !== '') {
      setSearchParams({
        view: nextView,
        searchFor: 'id',
        search: appliedSearch.search,
      }, { replace: true });
    }
  }

  return (
    <>
      <div className="flex gap-16 graph-labels">
        <h1 className={view === "paid" ? 'active-graph' : ''} onClick={() => handleViewChange('paid')}>Pago</h1>
        <h1 className={view === "topay" ? 'active-graph' : ''} onClick={() => handleViewChange('topay')}>A Pagar</h1>
        {/* <h1 className={view === "contracts" ? 'active-graph' : ''} onClick={() => setView('contracts')}>Contratos</h1> */}
      </div>
      <div className="flex justify-content-center align-center gap-8 margin-top-12 timeline-filters align-center">
        <span>Filtrar por:</span>
        <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => {
          setLevelToGet(e.target.value)
          setItemToFilter('')
        }} value={levelToGet}>
          <option value='' disabled>Selecione</option>
          <option value='units'>Unidade Construtiva</option>
          <option value='cells'>Célula</option>
          <option value='steps'>Etapa</option>
          <option value='substeps'>Subetapa</option>
          <option value='services'>Serviço</option>
        </select>
        {levelItems.length > 0 && <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={handleWbsSearch} value={itemToFilter} defaultChecked=''>
          <option value='' disabled>Selecione</option>
          {levelItems.map(i => {
            return <option key={i.wbs_code_id} value={i.wbs_code_id}>{i.building_unit_description || i.wbs_description}</option>
          })}
        </select>}
        {<div className="flex gap-8 align-center">
          {(itemToFilter !== '' || levelToGet !== "") && <button className="button button-error small" style={{ justifySelf: 'center' }} onClick={handleWbsReset}>Resetar</button>}
        </div>}
      </div>
      <div className="flex margin-top-16 margin-bottom-4 justify-space-between gap-16 timeline-filters align-center">
        <input aria-label="Buscar item" type="text" value={search.search} name="search" onChange={handleEditSearch} placeholder="Buscar item" />
        {search.search !== '' &&
          <>
            <select aria-label="Buscar por" value={search.searchFor || 'dis'} name="searchFor" onChange={handleEditSearch} >
              <option disabled value='dis'>Buscar por:</option>
              <option value='creditor_name'>Fornecedor</option>
              <option value='id'>Número</option>
            </select>
            <button disabled={search.search === "" || search.searchFor === ""} className="button button-accent small" style={{ justifySelf: 'center' }} onClick={handleFilter}>Buscar</button>
          </>
        }
        <select value={docFilter} onChange={(e) => setDocFilter(e.target.value)}>
          <option value="" disabled>Filtrar por documento</option>
          {documents?.map(i => {
            return <option key={i} value={i}>{i}</option>
          })}
        </select>
        <div className="date-range">
          <span className="label">Início</span>
          <input type="date" onChange={handleDateChange} value={dateLimit.min} name="min" max={new Date().toISOString().split('T')[0]} />
          <span className="label">Fim</span>
          <input type="date" onChange={handleDateChange} value={dateLimit.max} name="max" max={new Date().toISOString().split('T')[0]} />
        </div>
        {<div className="flex gap-8 align-center">
          {(dateLimit.min !== 0 || dateLimit.max !== 0 || docFilter !== '' || search.search !== '' || appliedSearch.search !== '') && <button className="button button-error small" style={{ justifySelf: 'center' }} onClick={handleReset}>Resetar</button>}
        </div>}
      </div>
      {(isFiltering) && <div className="flex justify-content-end margin-bottom-8">
        <button className="button button-accent small" onClick={handleDownload}>Extrair relatório</button>
      </div>}

      {resources?.map((i) => (
        <Item key={i._id} index={i._id} resource={i} paid={view === "paid"} />
      ))}
    </>
  );
};

export default ItemsTimeline;
